import { ModalClose } from '@startuptools/ui/ModalClose';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { type SubmitHandler } from 'react-hook-form';
import { Button } from '@startuptools/ui/Button';
import { type GqlMeeting } from '../../../../graphql/base-types.graphql';
import { useEditMeetingDialogSuspenseQuery } from './EditMeetingDialog.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { type FormValues, MeetingDetailsForm } from './MeetingDetailsForm';
import { DateTime } from 'luxon';

export const EditMeetingDialog = ({
  onClose,
  meeting,
  onSubmit,
}: {
  onClose: () => void;
  meeting: Pick<GqlMeeting, 'id' | 'number' | 'kind' | 'heldKind' | 'phone' | 'place' | 'startTime' | 'endTime'>;
  onSubmit: SubmitHandler<FormValues>;
}) => {
  const companyId = useCompanyId();

  const {
    data: { company, meetings },
  } = useEditMeetingDialogSuspenseQuery({
    variables: { companyId },
    fetchPolicy: 'no-cache',
  });

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: 532, minHeight: 592 }}>
        <ModalClose />
        <Stack gap={2} flexGrow={1}>
          <Typography level="h4">
            <Trans id="Edit Meeting" />
          </Typography>

          <MeetingDetailsForm
            company={company}
            meetings={meetings.models}
            onSubmit={onSubmit}
            formDefaults={{
              number: meeting.number ?? null,
              heldKind: meeting.heldKind,
              place: meeting.place ?? null,
              phone: meeting.phone ?? null,
              startTime: DateTime.fromISO(meeting.startTime),
              endTime: meeting.endTime ? DateTime.fromISO(meeting.endTime) : null,
            }}
            meetingKind={meeting.kind}
            usesStepper={false}
            isEditing
            actions={loading => (
              <Stack flexGrow={1} justifyContent="end">
                <Stack direction="row" gap={1} justifyContent="end">
                  <Button variant="outlined" color="neutral" onClick={onClose} disabled={loading}>
                    <Trans id="Cancel" />
                  </Button>
                  <Button type="submit" loading={loading}>
                    <Trans id="Save" />
                  </Button>
                </Stack>
              </Stack>
            )}
          />
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
